/**
 * study.js —— 学习乐园 · 全站脚本（主题 + 返回顶部 + 公共小件）
 * ---------------------------------------------------------------------------
 * ① 主题切换（页头 #st-theme；键 tb-theme 与工具站/台账/转念/一页纸共享，一处切换全站生效）
 * ② 返回顶部（滚动超一屏出现，结构与 decide.js 同口径）
 * ③ 公共小件出口 window.ST_UI（game-*.js 复用，避免每份游戏拷贝一套小工具）
 *
 * 游戏本体不在这里：每个游戏的界面与流程在 js/game-<slug>.js，由 layout/footer 只在
 * 游戏页引入；棋类的搜索与评估在 js/chess-engine.js。study.js 全页加载但只做以上三件事。
 *
 * 纪律（承台账/转念）：动态内容一律 createElement + textContent（天然防注入）；
 * 同状态零 DOM 写入；所有对局与练习数据只写 localStorage，不发任何网络请求。
 */
(function (global) {
  'use strict';

  var doc = global.document;
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function el(tag, cls) {
    var node = doc.createElement(tag);
    if (cls) { node.className = cls; }
    return node;
  }
  function span(cls, text) {
    var node = el('span', cls);
    node.textContent = text;
    return node;
  }
  function iconEl(name) {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'tool-icon');
    svg.setAttribute('aria-hidden', 'true');
    var use = doc.createElementNS(SVG_NS, 'use');
    use.setAttribute('href', '#i-' + name);
    svg.appendChild(use);
    return svg;
  }
  function toast(msg) {
    var UI = global.ToolUI || {};
    if (UI.toast) { UI.toast(msg); }
  }

  /* 走子轨迹：棋盘是等宽 8×8 grid（格边长 = --st-sq，无 gap），所以起点到落点的位移就是
     两格坐标差 × 格宽——不用量矩形。渲染刚把 64 格重建完，落点上的子先按位移画在起点格，
     强制回流后再归零，transition 就把它滑过去。马也照直线滑（lichess 同口径）。
     系统要求「减少动态效果」时直接跳过：子出现在落点即可，不影响读局。 */
  var FILES = 'abcdefgh';
  function flyPiece(boardBox, from, to) {
    if (!boardBox || !from || !to) { return; }
    if (global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }
    var sq = boardBox.querySelector('[data-sq="' + to + '"]');
    if (!sq) { return; }
    var piece = sq.querySelector('.st-piece');
    if (!piece) { return; }
    var dx = FILES.indexOf(from.charAt(0)) - FILES.indexOf(to.charAt(0));
    var dy = Number(to.charAt(1)) - Number(from.charAt(1));
    if (isNaN(dx) || isNaN(dy) || (dx === 0 && dy === 0)) { return; }
    piece.style.transform = 'translate(calc(' + dx + ' * var(--st-sq)),'
      + ' calc(' + dy + ' * var(--st-sq)))';
    sq.className += ' is-fly';
    void sq.offsetWidth;                 // 让上面的位移先落地，否则与归零并进同一帧 = 没有动画
    piece.className += ' is-fly';
    piece.style.transform = 'none';
  }

  /* 公共小件出口（game-*.js 复用） */
  global.ST_UI = { el: el, span: span, icon: iconEl, toast: toast, fly: flyPiece };

  /* ---------------- ① 主题 ---------------- */
  function initTheme() {
    var btn = doc.getElementById('st-theme');
    if (!btn) { return; }
    function label() {
      var dark = doc.documentElement.getAttribute('data-theme') === 'dark';
      btn.setAttribute('aria-label', dark ? '切换到浅色主题' : '切换到深色主题');
      btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
    }
    label();
    btn.addEventListener('click', function () {
      var dark = doc.documentElement.getAttribute('data-theme') === 'dark';
      var next = dark ? 'light' : 'dark';
      doc.documentElement.setAttribute('data-theme', next);
      try { global.localStorage.setItem('tb-theme', next); } catch (e) { /* 隐私模式不持久化 */ }
      label();
    });
  }

  /* ---------------- ② 返回顶部（对齐 tools/CMS：悬浮按钮 + 滚动进度环） ---------------- */
  function initBackTop() {
    var wrap = el('div', 'tool-float');
    var btn = el('button', 'tool-float-btn');
    btn.type = 'button';
    btn.setAttribute('aria-label', '回到顶部');
    btn.setAttribute('title', '回到顶部');
    var ring = doc.createElementNS(SVG_NS, 'svg');
    ring.setAttribute('class', 'tool-float-ring');
    ring.setAttribute('viewBox', '0 0 48 48');
    ring.setAttribute('aria-hidden', 'true');
    var ringCls = ['tool-float-ring-bg', 'tool-float-ring-pg'];
    var pg = null;
    for (var i = 0; i < ringCls.length; i++) {
      var c = doc.createElementNS(SVG_NS, 'circle');
      c.setAttribute('class', ringCls[i]);
      c.setAttribute('cx', '24');
      c.setAttribute('cy', '24');
      c.setAttribute('r', '22');
      if (i === 1) { pg = c; }
      ring.appendChild(c);
    }
    btn.appendChild(ring);
    btn.appendChild(iconEl('arrow-up'));
    wrap.appendChild(btn);
    doc.body.appendChild(wrap);
    btn.addEventListener('click', function () {
      global.scrollTo({ top: 0, behavior: 'smooth' });
    });
    var threshold = 300;
    var CIRC = 138.2; // 2π×22，进度环周长（与 tools.css stroke-dasharray 一致）
    var lastPct = -1;
    function sync() {
      var top = global.pageYOffset || doc.documentElement.scrollTop || 0;
      var show = top > threshold;
      var max = doc.documentElement.scrollHeight - global.innerHeight;
      var pct = max > 0 ? Math.min(Math.max(top / max, 0), 1) : 0;
      var pctChanged = pct !== lastPct;
      if (pctChanged) { lastPct = pct; }
      btn.className = btn.className.replace(/\s*is-show/, '') + (show ? ' is-show' : '');
      if (pctChanged) { pg.style.strokeDashoffset = (CIRC * (1 - pct)).toFixed(1); }
    }
    global.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  function boot() {
    initTheme();
    initBackTop();
  }
  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
